import React from "react";
import { Modal } from "react-bootstrap";
import { X, CheckCircleFill, GeoAltFill, Calendar3 } from "react-bootstrap-icons";
import moment from "moment";
import ReactPixel from "react-facebook-pixel";
import { trackEvent } from "../../services/events";
import "./QuickSearchModal.css";

interface QuickSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message: string;
  selectedProduct?: any;
  onRegister?: () => void;
  locationName?: string;
  startDate?: string | Date;
  endDate?: string | Date;
}

const QuickSearchModal: React.FC<QuickSearchModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  message,
  selectedProduct,
  onRegister,
  locationName,
  startDate,
  endDate,
}) => {
  if (!isOpen) return null;

  const handleTrackButtonClick = (buttonName: string) => {
    trackEvent({
      event_name: {
        web: `Kirkos_QuickSearchModal_${buttonName}_Click`,
        app: `Kirkos_QuickSearchModal_${buttonName}_Click_App`,
      },
      event_category: "Modal_Button_Click",
      module: "QuickSearchModal",
      payload: { button_name: buttonName },
    }).catch(() => { });

    if (process.env.REACT_APP_META_PIXEL_ID) {
      try {
        ReactPixel.track("ButtonClick", {
          content_name: buttonName,
          content_category: "QuickSearchModal Buttons",
          content_type: "Button Click",
          value: 0,
          currency: "AED",
        });
      } catch (_) { }
    }
  };

  const getDaysDiff = () => {
    if (!startDate || !endDate) return null;
    const start = moment(startDate);
    const end = moment(endDate);
    const diff = end.diff(start, "days");
    return diff > 0 ? diff : 1;
  };

  const days = getDaysDiff();
  const dateRangeStr =
    startDate && endDate
      ? `${moment(startDate).format("D MMM")} to ${moment(endDate).format("D MMM")}`
      : "";

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      contentClassName="border-0 shadow-lg quick-search-modal-content"
      dialogClassName="quick-search-modal-dialog"
      style={{ zIndex: 1055 }}
    >
      <div className="quick-search-modal-container">
        <button
          onClick={onClose}
          className="quick-search-close-btn"
          aria-label="Close booking login modal"
        >
          <X size={20} />
        </button>

        <div className="quick-search-modal-scroll-area">
          <div className="quick-search-modal-header">
            <div className="quick-search-modal-eyebrow">Kirkos account access</div>
            <h2 className="quick-search-modal-title">
              {selectedProduct ? "Continue your booking" : "Login Now"}
            </h2>
            <p className="quick-search-modal-subtitle">
              {selectedProduct
                ? "Sign in or sign up to complete your reservation and continue with booking confirmation."
                : message}
            </p>
          </div>

          {selectedProduct && (
            <div className="quick-search-product-card">
              <div className="quick-search-product-summary">
                <div className="quick-search-product-copy text-center w-100">
                  <h3 className="quick-search-car-name text-center">
                    {selectedProduct.product_name}
                  </h3>
                  <div className="quick-search-car-meta d-flex justify-content-center flex-wrap gap-3 mt-2">
                    <div className="meta-item d-flex align-items-center gap-1">
                      <GeoAltFill size={11} className="text-warning" />
                      <span>{locationName || "United Arab Emirates"}</span>
                    </div>
                    {dateRangeStr && (
                      <div className="meta-item d-flex align-items-center gap-1">
                        <Calendar3 size={11} className="text-warning" />
                        <span>{dateRangeStr}</span>
                        {days && <span className="ms-1 op-7">({days} {days === 1 ? "day" : "days"})</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="price-breakdown-grid mt-3">
                {[
                  { label: "Day", price: selectedProduct.product_details_car?.price_per_day },
                  { label: "Week", price: selectedProduct.product_details_car?.price_per_week },
                  { label: "Month", price: selectedProduct.product_details_car?.price_per_month },
                ].map((item, idx) => (
                  <div key={idx} className="price-card">
                    <div className="price-card-label">{item.label}</div>
                    <div className="price-card-value">
                      {item.price ? `AED ${Math.round(item.price)}` : "N/A"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedProduct && (
            <div className="benefits-list-container">
              {[
                "Save your booking progress",
                "Access reservation details anytime",
                "Faster checkout for future bookings",
              ].map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <div className="benefit-icon">
                    <CheckCircleFill size={10} color="white" />
                  </div>
                  <span className="benefit-text">{benefit}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="action-buttons-container">
          <button onClick={() => { handleTrackButtonClick("Login"); onConfirm(); }} className="quick-search-login-btn">
            Login
          </button>
          {onRegister && (
            <button onClick={() => { handleTrackButtonClick("Register"); onRegister(); }} className="quick-search-register-btn">
              Register
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default QuickSearchModal;
